/* eslint-disable @typescript-eslint/no-explicit-any */
import type {
  AccountInfo,
  IPublicClientApplication,
} from "@azure/msal-browser";
import { scopes } from "../authentication/PublicClientApplication";
import { API_BASE_URL } from "../constants";
import type { ArbeitspaketItem } from "../models/ArbeitspaketItem";
import type { ArbeitszeitItem } from "../models/ArbeitszeitItem";
import type { BerichtsTerminItem } from "../models/BerichtsTerminItem";
import type { ChangeStatusForschungsschwerpunktRequest } from "../models/ChangeStatusForschungsschwerpunktRequest";
import type { DocumentTemplateItem } from "../models/DocumentTemplateItem";
import type { FoerdergeberItem } from "../models/FoerdergeberItem";
import type { HeadOfInstituteItem } from "../models/HeadOfInstituteItem";
import type { HistoryItem } from "../models/HistoryItem";
import type { IDataItem } from "../models/IDataItem";
import type { IkbFinanzdatenItem } from "../models/IkbFinanzdatenItem";
import type { ImportKalkulationRequest } from "../models/ImportKalkulationRequest";
import type { InfoGroupItem } from "../models/InfoGroupItem";
import type { IUserPermissionItem } from "../models/IUserPermissionItem";
import type { KalkulationImportData } from "../models/KalkulationImportData";
import type { KalkulationImportResult } from "../models/KalkulationImportResult";
import type { KostenErloeseSchemaItem } from "../models/KostenErloeseSchemaItem";
import type { PlanwertKostenErloeseItem } from "../models/PlanwertKostenErloeseItem";
import type { PlanwertSollstundenProjektmitarbeiterItem } from "../models/PlanwertSollstundenProjektmitarbeiterItem";
import type { ProjectUserInfo } from "../models/ProjectUserInfo";
import type { ProjektArtItem } from "../models/ProjektArtItem";
import type { ProjektFilterItem } from "../models/ProjektFilterItem";
import type { ProjektFilterRequest } from "../models/ProjektFilterRequest";
import type { ProjektForschungsschwerpunktItem } from "../models/ProjektForschungsschwerpunktItem";
import type { ProjektForschungsschwerpunktItemOefos } from "../models/ProjektForschungsschwerpunktItemOefos";
import type { ProjektInstitutItem } from "../models/ProjektInstitutItem";
import type { ProjektItem } from "../models/ProjektItem";
import type { ProjektKontaktItem } from "../models/ProjektKontaktItem";
import type { ProjektKostenItem } from "../models/ProjektKostenItem";
import type { ProjektmitarbeiterItem } from "../models/ProjektmitarbeiterItem";
import type { ProjektPartnerItem } from "../models/ProjektPartnerItem";
import type { ProjektPartnerZuweisungItem } from "../models/ProjektPartnerZuweisungItem";
import type { ProjektRechnungKontoItem } from "../models/ProjektRechnungKontoItem";
import type { ProjektRisikoBewertungItem } from "../models/ProjektRisikoBewertungItem";
import type { ProjektRisikoItem } from "../models/ProjektRisikoItem";
import type { RechnungItem } from "../models/RechnungItem";
import type { SetArchivRequest } from "../models/SetArchivRequest";
import type { SetFreigabeRequest } from "../models/SetFreigabeRequest";
import type { SollIstVergleichItem } from "../models/SollIstVergleichItem";
import type { StundensatzItem } from "../models/StundensatzItem";

export class DataService {
  private instance: IPublicClientApplication;
  private account: AccountInfo;

  constructor(instance: IPublicClientApplication, account: AccountInfo) {
    this.instance = instance;
    this.account = account;
  }

  async fetchWithAuth(url: string, options: RequestInit = {}) {
    let lastError: Error | null = null;
    const maxRetries = 2; // One retry for transient network errors

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const response = await this.instance.acquireTokenSilent({
          scopes,
          account: this.account,
        });
        const headers = {
          ...options.headers,
          Authorization: `Bearer ${response.accessToken}`,
        };
        const fetchResult = await fetch(url, {
          ...options,
          headers,
          signal: options.signal,
        });

        // If successful, return immediately
        if (fetchResult.ok || fetchResult.status === 404) {
          return fetchResult;
        }

        // For certain errors, don't retry
        if (fetchResult.status === 401 || fetchResult.status === 403) {
          return fetchResult;
        }

        // For other errors, save and potentially retry
        lastError = new Error(
          `HTTP ${fetchResult.status}: ${fetchResult.statusText}`,
        );

        if (attempt < maxRetries - 1) {
          console.warn(`⚠️ Fetch attempt ${attempt + 1} failed, retrying...`);
          await new Promise((resolve) =>
            setTimeout(resolve, 500 * (attempt + 1)),
          );
        }

        return fetchResult;
      } catch (error: any) {
        lastError = error instanceof Error ? error : new Error(String(error));

        // If silent token acquisition fails, try interactive
        if (
          error?.errorCode === "interaction_required" ||
          error?.message?.includes("interaction_required") ||
          error?.message?.includes("AADSTS160021")
        ) {
          console.warn("Session expired. Attempting interactive login...");
          try {
            const response = await this.instance.acquireTokenPopup({
              scopes,
              account: this.account,
            });
            const headers = {
              ...options.headers,
              Authorization: `Bearer ${response.accessToken}`,
            };
            return fetch(url, { ...options, headers, signal: options.signal });
          } catch (interactiveError) {
            console.error(
              "Interactive token acquisition failed:",
              interactiveError,
            );
            // Dispatch event to notify the app that re-login is needed
            window.dispatchEvent(
              new CustomEvent("auth-required", {
                detail: {
                  message:
                    "Session expired. Please refresh the page and login again.",
                },
              }),
            );
            throw new Error(
              "Session expired. Please refresh the page and login again.",
            );
          }
        }

        // For network errors, try once more
        if (
          attempt < maxRetries - 1 &&
          (error?.message?.includes("Failed to fetch") ||
            error?.message?.includes("ERR_") ||
            error?.name === "TypeError")
        ) {
          console.warn(
            `⚠️ Network error on attempt ${attempt + 1}, retrying...`,
          );
          await new Promise((resolve) =>
            setTimeout(resolve, 500 * (attempt + 1)),
          );
          continue;
        }

        throw lastError;
      }
    }

    throw lastError || new Error("Fetch operation failed");
  }

  async getUserInfo(): Promise<ProjectUserInfo> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getuserinfo`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get user info: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjectUserInfo>;
  }

  async getAppRoles(): Promise<IDataItem[]> {
    const url = `${API_BASE_URL}/fhelix/general/permission/rollen?appId=2`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<IDataItem[]>;
  }

  async deleteUserRole(itemId: number): Promise<boolean> {
    const url = `${API_BASE_URL}/fhelix/general/permission/delete?itemId=${itemId}`;
    const res = await this.fetchWithAuth(url, {
      method: "DELETE",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return true;
  }

  async addUserRole(item: IUserPermissionItem): Promise<boolean | string> {
    const url = `${API_BASE_URL}/fhelix/general/permission/add`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    } else {
      return res.json() as Promise<boolean | string>;
    }
  }

  async getRolesAppPermissions(
    rollenId: string,
  ): Promise<IUserPermissionItem[]> {
    const url = `${API_BASE_URL}/fhelix/general/permission/rolesapppermissions?appId=2&rollenId=${rollenId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<IUserPermissionItem[]>;
  }

  async getUserAppPermissions(
    userUpn: string,
    appId: string,
  ): Promise<IUserPermissionItem[]> {
    console.log(
      `DataService.getUserAppPermissions called with userUpn=${userUpn}, appId=${appId}, account=${this.account.username}, ${API_BASE_URL}`,
    );
    const url = `${API_BASE_URL}/fhelix/general/permission/userpermissions?userUpn=${userUpn}&appId=${appId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<IUserPermissionItem[]>;
  }

  async getMitarbeiterSearchByUpn(upn: string): Promise<IDataItem[]> {
    const url = `${API_BASE_URL}/fhelix/personal/mitarbeitersearchbyupn?search=${upn}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    } else {
      return res.json() as Promise<IDataItem[]>;
    }
  }

  // ==================== Projekt Service Methods ====================

  // Get Methods
  async getProjektMeta(projektId: number): Promise<ProjektItem> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektmeta?projektId=${projektId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get project meta: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektItem>;
  }

  async getProjekt(projektId: number): Promise<ProjektItem> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojekt?projektId=${projektId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get project: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektItem>;
  }

  async getProjektListeWithoutDetails(): Promise<ProjektItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektlistewithoudetails`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get project list: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektItem[]>;
  }

  async getProjektListeWithoutDetailsFiltered(
    filter: string,
  ): Promise<ProjektItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektlistewithoudetails/filter?filter=${encodeURIComponent(filter)}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get filtered project list: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektItem[]>;
  }

  async getProjektListeWithoutDetailsByInstitution(
    institutId: number,
    studienjahr: number,
    signal?: AbortSignal,
  ): Promise<ProjektItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektlistewithoudetails/byinstitution?institutId=${institutId}&studienjahr=${studienjahr}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
      signal,
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get project list by institution: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektItem[]>;
  }

  async getProjektListeWithoutDetailsByIds(
    request: ProjektFilterRequest,
  ): Promise<ProjektItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektlistewithoudetails/byids`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get project list by ids: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektItem[]>;
  }

  async getProjektListe(
    getAlleProjekte: boolean = false,
    signal?: AbortSignal,
  ): Promise<ProjektItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektliste?getAlleProjekte=${getAlleProjekte}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
      signal,
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get project list: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektItem[]>;
  }

  async getProjektListeFiltered(filter: string): Promise<ProjektItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektliste/filter?filter=${encodeURIComponent(filter)}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get filtered project list: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektItem[]>;
  }

  async getProjektListeAdvanced(
    filter: ProjektFilterItem[],
    getSollIstVergleich: boolean = false,
  ): Promise<ProjektItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektliste/advanced?getSollIstVergleich=${getSollIstVergleich}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(filter),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get advanced project list: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektItem[]>;
  }

  async getProjektListeForCurrentUser(
    signal?: AbortSignal,
  ): Promise<ProjektItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektlisteforcurrentuser`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
      signal,
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get project list for current user: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektItem[]>;
  }

  async getProjektListeArchiviert(): Promise<ProjektItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektliste/archiviert`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get archived project list: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektItem[]>;
  }

  async getProjektListeArchiviertFiltered(
    filter: ProjektFilterRequest,
  ): Promise<ProjektItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektliste/archiviert/filtered`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(filter),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving archived project list: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektItem[]>;
  }

  async getProjektListeForBis(projektIds: number[]): Promise<ProjektItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektlisteforbis`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(projektIds),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get project list for BIS: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektItem[]>;
  }

  async getProjektListePaged(
    pageNumber: number = 1,
    pageSize: number = 10,
    userId: number = 0,
  ): Promise<ProjektItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektlistepaged?pageNumber=${pageNumber}&pageSize=${pageSize}&userId=${userId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get paged project list: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektItem[]>;
  }

  async getProjektListeMeta(): Promise<ProjektItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektlistemeta`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get project list meta: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektItem[]>;
  }

  async getProjektByGuid(projektGuid: string): Promise<ProjektItem> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektbyguid?projektGuid=${encodeURIComponent(projektGuid)}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get project by GUID: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektItem>;
  }

  async getProjektByKtrNummer(
    kostentraegerNummer: string,
  ): Promise<ProjektItem> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektbyktrnummer?kostentraegerNummer=${encodeURIComponent(kostentraegerNummer)}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get project by KTR number: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektItem>;
  }

  async existsProjektTitle(projektTitle: string): Promise<boolean> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/existsprojekttitle?projektTitle=${encodeURIComponent(projektTitle)}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error checking project title: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<boolean>;
  }

  // Create / Update Methods
  async addProjekt(projekt: ProjektItem): Promise<number> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/addprojekt`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(projekt),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error creating project: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<number>;
  }

  async updateProjekt(
    projekt: ProjektItem,
  ): Promise<{ success: boolean; message: string }> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/updateprojekt`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(projekt),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      return {
        success: false,
        message:
          errorText ||
          `Error updating project: ${res.status} ${res.statusText}`,
      };
    }
    return {
      success: true,
      message: "Project updated successfully",
    };
  }

  async systemUpdateProjekt(projekt: ProjektItem): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/systemupdateprojekt`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(projekt),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error in system update: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async updateProjektIsNecessaryDataAvailable(
    projekt: ProjektItem,
    isAllNecessaryDataAvailable: boolean,
  ): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/updateprojektisneccessarydataavailable?isAllNecessaryDataAvailable=${isAllNecessaryDataAvailable}`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(projekt),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error updating project data availability: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async setProjektfreigabeAngefordert(projekt: ProjektItem): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/setprojektfreigabeangefodert`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(projekt),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error requesting project approval: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async setProjektfreigabe(request: SetFreigabeRequest): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/setprojektfreigabe`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error setting project approval: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async setProjektende(projekt: ProjektItem): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/setprojektende`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(projekt),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error setting project end date: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async setProjektendeReset(projekt: ProjektItem): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/setprojektendeReset`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(projekt),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error resetting project end date: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async createFolgeprojekt(projekt: ProjektItem): Promise<number> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/createfolgeprojekt`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(projekt),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error creating follow-up project: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<number>;
  }

  async setProjektArchiviert(request: SetArchivRequest): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/setprojektarchiviert`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error archiving project: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async setProjektWebsitefreigabeAngefordert(
    projekt: ProjektItem,
  ): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/setprojektwebsitefreigabeangefodert`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(projekt),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error requesting website approval: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  // ==================== Project Creation Helper Methods ====================

  async getProjektPartnerSearch(
    searchTerm: string,
  ): Promise<ProjektPartnerItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektpartnersearch?searchTerm=${encodeURIComponent(searchTerm)}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error searching for partners: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektPartnerItem[]>;
  }

  async getProjektKontakte(partnerId: number): Promise<ProjektKontaktItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektkontakte?partnerId=${partnerId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving contacts: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektKontaktItem[]>;
  }

  async getProjektInstitute(): Promise<ProjektInstitutItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektinstitute`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving institutes: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektInstitutItem[]>;
  }

  async getProjektArtenArchiv(): Promise<ProjektArtItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektartenarchiv`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving project types for archive: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektArtItem[]>;
  }

  async getProjektArten(abteilungId: number): Promise<ProjektArtItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektarten?abteilungId=${abteilungId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving project types: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektArtItem[]>;
  }

  // ==================== Projektdokumente Methods ====================

  async getProjektdokumentListeSystem(projektId: number): Promise<any[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektdokumentlistesystem?projektId=${projektId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving project documents (system): ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<any[]>;
  }

  async getProjektdokumentListe(projektId: number): Promise<any[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektdokumentliste?projektId=${projektId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving project documents: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<any[]>;
  }

  async getProjektdokument(dokumentId: number): Promise<any> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektdokument?dokumentId=${dokumentId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving project document: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<any>;
  }

  async getProjektdokumentByGuid(dokumentGuid: string): Promise<any> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektdokumentbyguid?dokumentGuid=${encodeURIComponent(dokumentGuid)}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving project document by GUID: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<any>;
  }

  async getProjektdokumentBinaries(dokumentGuid: string): Promise<Blob> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektdokumentbinaries?dokumentGuid=${encodeURIComponent(dokumentGuid)}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving document binaries: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.blob() as Promise<Blob>;
  }

  async addProjektdokument(dokument: any, dateValue?: Date): Promise<number> {
    const query = dateValue
      ? `?dateValue=${encodeURIComponent(
          `${dateValue.getFullYear()}-${String(dateValue.getMonth() + 1).padStart(2, "0")}-${String(dateValue.getDate()).padStart(2, "0")}`,
        )}`
      : "";
    const url = `${API_BASE_URL}/fhelix/imcprojects/addprojektdokument${query}`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(dokument),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error adding project document: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<number>;
  }

  async updateProjektdokument(dokument: any): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/updateprojektdokument`;
    const res = await this.fetchWithAuth(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(dokument),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error updating project document: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async deleteProjektdokument(dokumentId: number): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/deleteprojektdokument?dokumentId=${dokumentId}`;
    const res = await this.fetchWithAuth(url, {
      method: "DELETE",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error deleting project document: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async deleteProjektdokumentByGuid(dokumentGuid: string): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/deleteprojektdokumentbyguid?dokumentGuid=${encodeURIComponent(dokumentGuid)}`;
    const res = await this.fetchWithAuth(url, {
      method: "DELETE",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error deleting project document by GUID: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async getProjektdokumentArbeitsbereich(
    projektId: number,
    folderPath: string,
    fileName: string,
  ): Promise<any> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektdokumentarbeitsbereich?projektId=${projektId}&folderPath=${encodeURIComponent(folderPath)}&fileName=${encodeURIComponent(fileName)}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving working area document: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<any>;
  }

  async addProjektdokumentArbeitsbereich(
    projektId: number,
    folderPath: string,
    fileName: string,
    file: File,
  ): Promise<string> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/addprojektdokumentarbeitsbereich`;
    const formData = new FormData();
    formData.append("projektId", projektId.toString());
    formData.append("folderPath", folderPath);
    formData.append("fileName", fileName);
    formData.append("file", file);

    const res = await this.fetchWithAuth(url, {
      method: "POST",
      body: formData,
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error adding working area document: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<string>;
  }

  async setGeheimhaltungserklaerung(
    projektId: number,
    isGeheimhaltungserklaerungOk: boolean,
  ): Promise<any> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/setgeheimhaltungserklaerung?projektId=${projektId}&isGeheimhaltungserklaerungOk=${isGeheimhaltungserklaerungOk}`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error setting confidentiality agreement: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.text() as Promise<any>;
  }

  // ==================== Projektmitarbeiter Methods ====================

  async addProjektmitarbeiter(request: any): Promise<any> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/addprojektmitarbeiter`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error adding project member: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<any>;
  }

  async removeProjektmitarbeiter(
    projektId: number,
    userID: number,
  ): Promise<boolean> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/removeprojektmitarbeiter?projektId=${projektId}&projektMitarbeiterId=${userID}`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error adding project member: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<any>;
  }

  async activateProjektmitarbeiter(
    projektmitarbeiterID: number,
  ): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/activateprojektmitarbeiter?projektmitarbeiterID=${projektmitarbeiterID}`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error activating project member: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async deactivateProjektmitarbeiter(
    projektmitarbeiterID: number,
  ): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/deactivateprojektmitarbeiter?projektmitarbeiterID=${projektmitarbeiterID}`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error deactivating project member: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async setNewProjektleiter(request: any): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/setnewprojektleiter`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error setting new project manager: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async assignProgrammeDirectorAsProjectManager(
    projektID: number,
    studiengangID: number,
  ): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/assignprogrammedirectorasprojectmanager?projektID=${projektID}&studiengangID=${studiengangID}`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error assigning programme director: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async isBenutzerProjektmitarbeiter(
    userID: number,
    projektID: number,
  ): Promise<boolean> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/isbenutzerproject%20mitarbeiter?userID=${userID}&projektID=${projektID}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error checking user project membership: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<boolean>;
  }

  async getProjektmitarbeiterAktiveListe(projektID: number): Promise<any[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektmitarbeiteraktive?projektID=${projektID}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving active project members: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<any[]>;
  }

  async getProjektmitarbeiterProjektstundenListe(
    projektID: number,
  ): Promise<ProjektmitarbeiterItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektmitarbeiterprojektstunden?projektID=${projektID}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving project member hours: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektmitarbeiterItem[]>;
  }

  async getProjektmitarbeiterProjektstundenListeZeitraum(
    projektID: number,
    dateVon: Date,
    dateBis: Date,
  ): Promise<ProjektmitarbeiterItem[]> {
    const vonStr = dateVon.toISOString().split("T")[0];
    const bisStr = dateBis.toISOString().split("T")[0];
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektmitarbeiterprojektstundenzeitraum?projektID=${projektID}&dateVon=${vonStr}&dateBis=${bisStr}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving project member hours for period: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektmitarbeiterItem[]>;
  }

  async getProjektmitarbeiterListe(
    projektID: number,
    rolle?: string,
  ): Promise<any[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektmitarbeiterliste?projektID=${projektID}${rolle ? `&rolle=${rolle}` : ""}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving project members: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<any[]>;
  }

  async getProjektmitarbeiter(itemId: number): Promise<any> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektmitarbeiter?itemId=${itemId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving project member: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<any>;
  }

  async updateProjektmitarbeiter(
    item: ProjektmitarbeiterItem,
    erweiterteRechte?: boolean,
  ): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/updateprojektmitarbeiter`;
    const request = {
      Item: item,
      ErweiterteRechte: erweiterteRechte,
    };
    const res = await this.fetchWithAuth(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error updating project member: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async getMitarbeiter(): Promise<ProjektmitarbeiterItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getmitarbeiter`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving employees: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektmitarbeiterItem[]>;
  }

  async changeZustaendiger(
    projektID: number,
    type: boolean,
    projektmitarbeiterID?: number,
  ): Promise<boolean> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/changezustaendiger?projektID=${projektID}&type=${type}${projektmitarbeiterID ? `&projektmitarbeiterID=${projektmitarbeiterID}` : ""}`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error changing responsibility: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return true;
  }

  // ==================== Planwert Sollstunden Projektmitarbeiter Methods ====================

  async getPlanwerteSollstundenProjektmitarbeiterListe(
    projektId: number,
  ): Promise<PlanwertSollstundenProjektmitarbeiterItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getplanwertsollstundenprojektmitarbeiterliste?projektId=${projektId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving planned hours: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<PlanwertSollstundenProjektmitarbeiterItem[]>;
  }

  async getPlanwertSollstundenProjektmitarbeiter(
    itemId: number,
  ): Promise<PlanwertSollstundenProjektmitarbeiterItem> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getplanwertsollstundenprojektmitarbeiter?itemId=${itemId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving planned hours item: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<PlanwertSollstundenProjektmitarbeiterItem>;
  }

  async addPlanwertSollstundenProjektmitarbeiter(
    item: PlanwertSollstundenProjektmitarbeiterItem,
  ): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/addplanwertsollstundenprojektmitarbeiter`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error adding planned hours: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async updatePlanwertSollstundenProjektmitarbeiter(
    item: PlanwertSollstundenProjektmitarbeiterItem,
  ): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/updateplanwertsollstundenprojektmitarbeiter`;
    const res = await this.fetchWithAuth(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error updating planned hours: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async deletePlanwertSollstundenProjektmitarbeiter(
    itemId: number,
  ): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/deleteplanwertsollstundenprojektmitarbeiter?itemId=${itemId}`;
    const res = await this.fetchWithAuth(url, {
      method: "DELETE",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error deleting planned hours: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async addOrUpdatePlanwertSollstundenKalkulation(
    items: PlanwertSollstundenProjektmitarbeiterItem[],
  ): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/addorupdateplanwertsollstundenkalkulation`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(items),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error adding or updating planned hours calculation: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async deletePlanwertSollstundenProjekt(projektId: number): Promise<void> {
    if (projektId <= 0) {
      throw new Error("Project ID must be greater than 0");
    }

    const url = `${API_BASE_URL}/fhelix/imcprojects/deleteplanwertsollstundenprojekt?projektId=${projektId}`;
    const res = await this.fetchWithAuth(url, {
      method: "DELETE",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error deleting planned work hours for project: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  // ==================== Arbeitspakete Methods ====================

  async addArbeitspaket(
    arbeitspaket: any,
    insertAfterId: number = -1,
  ): Promise<number> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/addarbeitspaket`;
    const request = {
      Arbeitspaket: arbeitspaket,
      InsertAfterId: insertAfterId,
      Meilenstein: null,
    };
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error adding work package: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<number>;
  }

  async getArbeitspaket(paketId: number): Promise<ArbeitspaketItem> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getarbeitspaket?paketId=${paketId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving work package: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ArbeitspaketItem>;
  }

  async getArbeitspaketListe(projektId: number): Promise<ArbeitspaketItem[]> {
    console.log("Fetching work package list for project ID:", projektId);
    const url = `${API_BASE_URL}/fhelix/imcprojects/getarbeitspakettliste?projektId=${projektId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving work packages: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ArbeitspaketItem[]>;
  }

  async updateArbeitspaket(
    arbeitspaket: ArbeitspaketItem,
    insertAfterId: number = -1,
  ): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/updatearbeitspaket`;
    const request = {
      Arbeitspaket: arbeitspaket,
      InsertAfterId: insertAfterId,
    };
    const res = await this.fetchWithAuth(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error updating work package: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async deleteArbeitspaket(paketId: number): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/deletearbeitspaket?paketId=${paketId}`;
    const res = await this.fetchWithAuth(url, {
      method: "DELETE",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error deleting work package: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async getArbeitspaketEbenenListe(
    projektId: number,
  ): Promise<ArbeitspaketItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getarbeitspaketebenen?projektId=${projektId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving work package levels: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ArbeitspaketItem[]>;
  }

  async updateArbeitspaketEbenen(
    projektId: number,
    prefixes: ArbeitspaketItem[],
  ): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/updatearbeitspaketebenen`;
    const request = {
      ProjektId: projektId,
      Prefixes: prefixes,
    };
    const res = await this.fetchWithAuth(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error updating work package levels: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  // ==================== Berichtstermine Methods ====================

  async addBerichtstermin(
    berichtstermin: any,
    insertAfterId: number = -1,
  ): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/addberichtstermin`;
    const request = {
      Berichtstermin: berichtstermin,
      InsertAfterId: insertAfterId,
    };
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error adding reporting deadline: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async getBerichtstermin(berichtId: number): Promise<any> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getberichtstermin?berichtId=${berichtId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving reporting deadline: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<any>;
  }

  async getBerichtsterminListe(
    projektId: number,
  ): Promise<BerichtsTerminItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getberichtsterminliste?projektId=${projektId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving reporting deadlines: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<BerichtsTerminItem[]>;
  }

  async updateBerichtstermin(
    bericht: BerichtsTerminItem,
    insertAfterId: number = -1,
  ): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/updateberichtstermin`;
    const request = {
      Berichtstermin: bericht,
      InsertAfterId: insertAfterId,
    };

    const res = await this.fetchWithAuth(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error updating reporting deadline: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async deleteBerichtstermin(berichtId: number): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/deleteberichtstermin?berichtId=${berichtId}`;
    const res = await this.fetchWithAuth(url, {
      method: "DELETE",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error deleting reporting deadline: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  // ==================== Meilensteine Methods ====================

  async addMeilenstein(
    meilenstein: any,
    insertAfterId: number = -1,
  ): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/addmeilenstein`;
    const request = {
      Meilenstein: meilenstein,
      InsertAfterId: insertAfterId,
    };
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error adding milestone: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async getMeilenstein(meilensteinId: number): Promise<any> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getmeilenstein?meilensteinId=${meilensteinId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving milestone: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<any>;
  }

  async getMeilensteinListe(projektId: number): Promise<any[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getmeilensteinliste?projektId=${projektId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving milestones: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<any[]>;
  }

  async updateMeilenstein(
    meilenstein: any,
    insertAfterId: number = -1,
  ): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/updatemeilenstein`;
    const request = {
      Meilenstein: meilenstein,
      InsertAfterId: insertAfterId,
    };
    const res = await this.fetchWithAuth(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error updating milestone: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async deleteMeilenstein(meilensteinId: number): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/deletemeilenstein?meilensteinId=${meilensteinId}`;
    const res = await this.fetchWithAuth(url, {
      method: "DELETE",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error deleting milestone: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  // ==================== Projektpartner Zuweisungen Methods ====================

  async getProjektpartnerListeAlleAktiven(
    projektID: number,
  ): Promise<unknown[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektpartnerlistealleaktiven?projektID=${projektID}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving active project partners: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<unknown[]>;
  }

  async getProjektpartnerListe(
    projektID: number,
    rolle: number,
  ): Promise<unknown[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektpartnerliste?projektID=${projektID}&rolle=${rolle}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving project partners by role: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<unknown[]>;
  }

  async getProjektpartnerListeAllForProject(
    projektID: number,
  ): Promise<unknown[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektpartnerlisteall?projektID=${projektID}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving all project partner assignments: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<unknown[]>;
  }

  async addProjektpartner(
    partnerItem: ProjektPartnerZuweisungItem,
  ): Promise<number> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/addprojektpartner`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(partnerItem),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error adding project partner: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<number>;
  }

  async removeProjektpartner(
    projektId: number,
    partnerId: number,
  ): Promise<boolean> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/removeprojektpartner?projektId=${projektId}&partnerId=${partnerId}`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error removing project partner: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<boolean>;
  }

  async getProjektpartner(partnerZuweisungId: number): Promise<unknown> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektpartner?partnerZuweisungId=${partnerZuweisungId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving project partner assignment: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<unknown>;
  }

  async activateProjektpartner(partnerZuweisungId: number): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/activateprojektpartner?partnerZuweisungId=${partnerZuweisungId}`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error activating project partner: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async deactivateProjektpartner(partnerZuweisungId: number): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/deactivateprojektpartner?partnerZuweisungId=${partnerZuweisungId}`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error deactivating project partner: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async updateProjektpartner(
    partnerItem: ProjektPartnerZuweisungItem,
  ): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/updateprojektpartner`;
    const res = await this.fetchWithAuth(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(partnerItem),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error updating project partner: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async deactivateAllProjektpartnerRole(
    projektID: number,
    rolle: number,
  ): Promise<unknown[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/deactivateallprojektpartnerrole?projektID=${projektID}&rolle=${rolle}`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error deactivating all partners in role: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<unknown[]>;
  }

  async deactivateOtherRolesProjektpartner(
    projektID: number,
    companyID: number,
    rolle: number,
  ): Promise<string> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/deactivateotherrolesprojektpartner?projektID=${projektID}&companyID=${companyID}&rolle=${rolle}`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error deactivating other partner roles: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<string>;
  }

  async updateProjektrolleIMC(
    projektId: number,
    rolleId: number,
  ): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/updateprojektrolleIMC?projektId=${projektId}&rolleId=${rolleId}`;
    const res = await this.fetchWithAuth(url, {
      method: "PUT",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error updating project role: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async getProjektpartnerListeForBis(projektID: number): Promise<unknown[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektpartnerlisteforbis?projektID=${projektID}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving project partners for BIS: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<unknown[]>;
  }

  // ==================== Projektpartner Base Methods ====================

  async getProjektpartnerListeAllCompanies(): Promise<ProjektPartnerItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektpartnerlistealleall`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving all project partner companies: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektPartnerItem[]>;
  }

  async getProjektpartnerListeBezeichnung(): Promise<ProjektPartnerItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektpartnerlistebezeichnung`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving project partners by designation: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektPartnerItem[]>;
  }

  async getProjektpartnerListeBezeichnungFiltered(
    filter: string,
  ): Promise<ProjektPartnerItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektpartnerlistebezeichnungfiltered?filter=${encodeURIComponent(filter)}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving filtered project partners: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektPartnerItem[]>;
  }

  async getProjektpartnerListeFiltered(
    filter: string[],
  ): Promise<ProjektPartnerItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektpartnerlistefiltered`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(filter),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving filtered project partners: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektPartnerItem[]>;
  }

  async getProjektpartnerById(id: number): Promise<ProjektPartnerItem> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektpartnerbyid?id=${id}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving project partner: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektPartnerItem>;
  }

  async getProjektpartnerByGuid(guid: string): Promise<ProjektPartnerItem> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektpartnerbyguid?guid=${encodeURIComponent(guid)}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving project partner by GUID: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektPartnerItem>;
  }

  async addOrUpdateProjektpartner(
    partnerItem: ProjektPartnerItem,
  ): Promise<number> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/addorupdateprojektpartner`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(partnerItem),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error adding/updating project partner: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<number>;
  }

  async markProjektpartnerForDeletion(
    companyId: number,
  ): Promise<ProjektPartnerItem> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/markprojektpartnerfordeletion?companyId=${companyId}`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error marking project partner for deletion: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektPartnerItem>;
  }

  async deleteProjektpartner(companyId: number): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/deleteprojektpartner?companyId=${companyId}`;
    const res = await this.fetchWithAuth(url, {
      method: "DELETE",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error deleting project partner: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async getAnsprechpartner(id: number): Promise<any> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getansprechpartner?id=${id}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving contact person: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<any>;
  }

  async addOrUpdateAnsprechpartner(contactItem: any): Promise<number> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/addorupdateansprechpartner`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(contactItem),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error adding/updating contact person: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<number>;
  }

  async mergeAnsprechpartner(request: any): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/mergeansprechpartner`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error merging contact persons: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async deleteAnsprechpartner(kontaktId: number): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/deleteansprechpartner?kontaktId=${kontaktId}`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error deleting contact person: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async mergeProjektpartner(request: any): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/mergeprojektpartner`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error merging project partners: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async isProjektpartnerMerged(companyId: number): Promise<boolean> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/isprojektpartnermerged?companyId=${companyId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error checking if project partner is merged: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<boolean>;
  }

  // ==================== Rechnungen und Partnerzahlungen Methods ====================

  async getRechnungenListe(projektID: number): Promise<RechnungItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getrechnungenliste?projektID=${projektID}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving invoices: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<RechnungItem[]>;
  }

  async getRechnung(rechnungID: number): Promise<RechnungItem> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getrechnung?rechnungID=${rechnungID}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving invoice: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<RechnungItem>;
  }

  async getRechnungByGuid(rechnungGuid: string): Promise<RechnungItem> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getrechnungbyguid?rechnungGuid=${encodeURIComponent(rechnungGuid)}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving invoice by GUID: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<RechnungItem>;
  }

  async addRechnung(rechnung: any): Promise<number> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/addrechnung`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(rechnung),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error adding invoice: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<number>;
  }

  async updateRechnung(rechnung: any): Promise<boolean> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/updaterechnung`;
    const res = await this.fetchWithAuth(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(rechnung),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error updating invoice: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<boolean>;
  }

  async getPartnerzahlungenListe(projektId: number): Promise<any[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getpartnerzahlungenliste?projektId=${projektId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving partner payments: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<any[]>;
  }

  async addPartnerzahlung(partnerzahlung: any): Promise<boolean> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/addpartnerzahlung`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(partnerzahlung),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error adding partner payment: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<boolean>;
  }

  async updatePartnerzahlung(partnerzahlung: any): Promise<boolean> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/updatepartnerzahlung`;
    const res = await this.fetchWithAuth(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(partnerzahlung),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error updating partner payment: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<boolean>;
  }

  async deleteRechnung(rechnungID: number): Promise<boolean> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/deleterechnung?rechnungID=${rechnungID}`;
    const res = await this.fetchWithAuth(url, {
      method: "DELETE",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error deleting invoice: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<boolean>;
  }

  async freigabeRechnung(rechnungId: number): Promise<boolean> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/freigaberechnung?rechnungId=${rechnungId}`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error requesting invoice approval: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return true;
  }

  async deletePartnerzahlung(partnerzahlungID: number): Promise<boolean> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/deletepartnerzahlung?rechnungID=${partnerzahlungID}`;
    const res = await this.fetchWithAuth(url, {
      method: "DELETE",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error deleting partner payment: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<boolean>;
  }

  // ==================== Teilbeträge Methods ====================

  async getTeilbetragRechnung(teilbetragId: number): Promise<any> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getteilbetragrechnung?teilbetragId=${teilbetragId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving partial amount: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<any>;
  }

  async addTeilbetragRechnung(teilbetrag: any): Promise<boolean> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/addteilbetragrechnung`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(teilbetrag),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error adding partial amount: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<boolean>;
  }

  async updateTeilbetragRechnung(teilbetrag: any): Promise<boolean> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/updateteilbetragrechnung`;
    const res = await this.fetchWithAuth(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(teilbetrag),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error updating partial amount: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<boolean>;
  }

  async deleteTeilbetragRechnung(teilbetragId: number): Promise<boolean> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/deleteteilbetragrechnung?teilbetragId=${teilbetragId}`;
    const res = await this.fetchWithAuth(url, {
      method: "DELETE",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error deleting partial amount: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<boolean>;
  }

  // ==================== Zahlungseingänge Methods ====================

  async addZahlungRechnung(zahlung: any): Promise<number | null> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/addzahlungrechnung`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(zahlung),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error adding payment: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<number | null>;
  }

  async getZahlungRechnung(zahlungId: number): Promise<any> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getzahlungrechnung?zahlungId=${zahlungId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving payment: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<any>;
  }

  async updateZahlungRechnung(zahlung: any): Promise<boolean> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/updatezahlungrechnung`;
    const res = await this.fetchWithAuth(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(zahlung),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error updating payment: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<boolean>;
  }

  async deleteZahlungRechnung(zahlungId: number): Promise<boolean> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/deletezahlungrechnung?zahlungId=${zahlungId}`;
    const res = await this.fetchWithAuth(url, {
      method: "DELETE",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error deleting payment: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<boolean>;
  }

  // ==================== Risikomanagement Methods ====================

  async addProjektRisiko(item: ProjektRisikoItem): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/addprojektrisiko`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error adding risk: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async updateProjektRisiko(
    risikoID: number,
    item: ProjektRisikoItem,
  ): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/updateprojektrisiko?risikoID=${risikoID}`;
    const res = await this.fetchWithAuth(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error updating risk: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async deleteProjektRisiko(
    projektId: number,
    risikoId: number,
  ): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/deleteprojektrisiko?projektId=${projektId}&risikoId=${risikoId}`;
    const res = await this.fetchWithAuth(url, {
      method: "DELETE",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error deleting risk: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async getProjektRisiko(projektID: number): Promise<ProjektRisikoItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektrisiko?projektID=${projektID}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving risks: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektRisikoItem[]>;
  }

  async getProjektRisikoItem(risikoID: number): Promise<ProjektRisikoItem> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektrisikoitem?risikoID=${risikoID}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving risk: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektRisikoItem>;
  }

  async checkCountProjektRisikoOk(projektId: number): Promise<boolean> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/checkcountprojektrisikook?projektId=${projektId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error checking risk count: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<boolean>;
  }

  async getRisikoUeberpruefungAusstaendig(
    projektID?: number,
  ): Promise<ProjektRisikoItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getriskoueberpruefungausstaendig${projektID ? `?projektID=${projektID}` : ""}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving pending risk reviews: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektRisikoItem[]>;
  }

  async getRisikoUeberpruefungAusstaendigProjektIds(
    projektId?: number,
  ): Promise<number[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getriskoueberpruefungausstaendigprojektids${projektId ? `?projektId=${projektId}` : ""}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving project IDs with pending risk reviews: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<number[]>;
  }

  async getProjektRisikoBewertungen(
    risikoID: number,
  ): Promise<ProjektRisikoBewertungItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektriskobewertungen?risikoID=${risikoID}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving risk assessments: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektRisikoBewertungItem[]>;
  }

  async addProjektRisikoUeberpruefung(
    risikoID: number,
    userID: number,
    projektID: number,
    anmerkung: string,
  ): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/addprojektriskoueberpruefung?risikoID=${risikoID}&userID=${userID}&projektID=${projektID}`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(anmerkung),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error adding risk review: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  // ==================== Schlagworte Methods ====================

  async addSchlagworteToProjekt(
    projektId: number,
    keywords: number[],
  ): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/addschlagworteToProjekt?projektId=${projektId}`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(keywords),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error adding keywords: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async deleteSchlagwortFromProjekt(
    projektId: number,
    keywordId: number,
  ): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/deleteschlagwortFromProjekt?projektId=${projektId}&keywordId=${keywordId}`;
    const res = await this.fetchWithAuth(url, {
      method: "DELETE",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error deleting keyword: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  // Teams
  async createTeamsForProjekt(projektId: number): Promise<string> {
    if (projektId <= 0) {
      throw new Error("Project ID must be greater than 0");
    }
    const url = `${API_BASE_URL}/fhelix/imcprojects/createTeamsForProjekt?projektId=${projektId}`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error creating teams: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    const result = await res.text();
    return result;
  }

  // ==================== History Methods ====================

  async addHistoryEntry(entry: HistoryItem): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/addhistoryentry`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(entry),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error adding history entry: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async getHistoryEntries(projektId: number): Promise<HistoryItem[]> {
    if (projektId <= 0) {
      throw new Error("Project ID must be greater than 0");
    }
    const url = `${API_BASE_URL}/fhelix/imcprojects/gethistoryentries?projektId=${projektId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving history entries: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<HistoryItem[]>;
  }

  // ==================== Dropdown Options Methods ====================

  async getNations(): Promise<IDataItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getnations`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving nations: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<IDataItem[]>;
  }

  async getFinanzPartnerCodes(): Promise<IDataItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getfinanzpartnercodes`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving financial partner codes: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<IDataItem[]>;
  }

  async getKooperationsPartnerCodes(): Promise<IDataItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getkooperationspartnercodes`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving cooperation partner codes: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<IDataItem[]>;
  }

  async getStudiengaenge(): Promise<IDataItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getstudiengaenge`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving study programs: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<IDataItem[]>;
  }

  async getNachhaltigkeitsziele(): Promise<IDataItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getnachhaltigkeitsziele`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving sustainability goals: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<IDataItem[]>;
  }

  async getFoerdergeberNeu(): Promise<IDataItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getfoerdergeberneu`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving funding agencies: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<IDataItem[]>;
  }

  async getFoerderschieneItems(): Promise<IDataItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getfoerderschieneitems`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving funding track items: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<IDataItem[]>;
  }

  async getForschungsschwerpunkte(): Promise<IDataItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getforschungsschwerpunkte`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving research focus areas: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<IDataItem[]>;
  }

  async getForschungsschwerpunkteOefos(): Promise<
    ProjektForschungsschwerpunktItemOefos[]
  > {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getforschungsschwerpunkteoefos`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving research focus areas: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektForschungsschwerpunktItemOefos[]>;
  }

  async getForschungsschwerpunkteList(): Promise<
    ProjektForschungsschwerpunktItem[]
  > {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getforschungsschwerpunktelist`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving research focus areas list: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektForschungsschwerpunktItem[]>;
  }

  async addForschungsschwerpunkt(
    item: ProjektForschungsschwerpunktItem,
  ): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/addforschungsschwerpunkt`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error adding research focus area: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async updateForschungsschwerpunkt(
    item: ProjektForschungsschwerpunktItem,
  ): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/updateforschungsschwerpunkt`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error updating research focus area: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async changeStatusForschungsschwerpunkt(
    request: ChangeStatusForschungsschwerpunktRequest,
  ): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/changeStatusForschungsschwerpunkt`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error changing research focus area status: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async getProjektStatusItems(): Promise<IDataItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektstatusitems`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving project status items: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<IDataItem[]>;
  }

  async getArbeitszeiten(projektId: number): Promise<ArbeitszeitItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getarbeitszeiten?projektId=${projektId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving work times: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ArbeitszeitItem[]>;
  }

  // #region Accounting

  async getGliederungsschemaListe(): Promise<KostenErloeseSchemaItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getgliederungsschema`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving cost classification schemas: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<KostenErloeseSchemaItem[]>;
  }

  async getGliederungsschema(id: number): Promise<KostenErloeseSchemaItem> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getgliederungsschema/${id}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving cost classification schema: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<KostenErloeseSchemaItem>;
  }

  async getGliederungsschemaPositionenList(
    schemaId: number,
  ): Promise<PlanwertKostenErloeseItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getgliederungsschemapositionenliste/${schemaId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving cost classification positions: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<PlanwertKostenErloeseItem[]>;
  }

  async getKostenstellenListe(): Promise<Record<string, string>> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getkostenstellenliste`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving cost centers: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<Record<string, string>>;
  }

  async getKostenstellennummer(kostentraegerNummer: string): Promise<string> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getkostenstellennummer/${kostentraegerNummer}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving cost center number: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<string>;
  }

  async getProjektKosten(
    projektId: number,
    kostentraegerNummer: string,
    schemaId: number = 1,
  ): Promise<ProjektKostenItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektkosten?projektId=${projektId}&kostentraegerNummer=${kostentraegerNummer}&schemaId=${schemaId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving project costs: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektKostenItem[]>;
  }

  async getProjektKostenBISMeldung(
    projektIds: number[],
    schemaId: number = 1,
  ): Promise<ProjektKostenItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektkosten/bismeldung?schemaId=${schemaId}`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(projektIds),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving BIS report project costs: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektKostenItem[]>;
  }

  async getProjektSollIstVergleich(
    projektId: number,
    kostentraegerNummer: string,
    start?: Date,
    ende?: Date,
    schemaId: number = 1,
  ): Promise<SollIstVergleichItem[]> {
    const startStr = start ? start.toISOString() : undefined;
    const endeStr = ende ? ende.toISOString() : undefined;
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektsollinstvergleich?projektId=${projektId}&kostentraegerNummer=${kostentraegerNummer}&start=${startStr}&ende=${endeStr}&schemaId=${schemaId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving plan vs actual comparison: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<SollIstVergleichItem[]>;
  }

  async getProjektSollIstVergleichFromBMD(
    kostentraegerNummer: string,
    start?: Date,
    end?: Date,
  ): Promise<SollIstVergleichItem[]> {
    const startStr = start ? start.toISOString() : undefined;
    const endStr = end ? end.toISOString() : undefined;
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektsollinstvergleichfrombmd?kostentraegerNummer=${kostentraegerNummer}&start=${startStr}&end=${endStr}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving plan vs actual comparison from BMD: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<SollIstVergleichItem[]>;
  }

  async getProjektkostenListeAlleBestellungen(
    projektId: number,
  ): Promise<PlanwertKostenErloeseItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektkostenlisteallebestellungen?projektId=${projektId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving all project cost orders: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<PlanwertKostenErloeseItem[]>;
  }

  async getProjektkostenListeAlleBestellungenOverview(
    projektId: number,
  ): Promise<PlanwertKostenErloeseItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektkostenlisteallebestellungenoverview?projektId=${projektId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving all project cost orders overview: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<PlanwertKostenErloeseItem[]>;
  }

  async getProjektkostenListe(
    projektId: number,
    schemaId: number = 1,
  ): Promise<ProjektKostenItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektkostenliste?projektId=${projektId}&schemaId=${schemaId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving project cost list: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektKostenItem[]>;
  }

  async getIkbErloeseItem(id: number): Promise<PlanwertKostenErloeseItem> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getikberloeseitem/${id}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving IKB revenue item: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<PlanwertKostenErloeseItem>;
  }

  async getIkbErloese(
    isBudget: boolean,
    jahr: number,
  ): Promise<PlanwertKostenErloeseItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getikberloese?isBudget=${isBudget}&jahr=${jahr}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving IKB revenues: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<PlanwertKostenErloeseItem[]>;
  }

  async addIkbErloeseBudget(
    items: PlanwertKostenErloeseItem[],
    jahr: number,
  ): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/addikberloesebdgt?jahr=${jahr}`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(items),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error adding IKB budget revenues: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async addIkbErloeseForecast(
    items: PlanwertKostenErloeseItem[],
    jahr: number,
    datum: Date,
  ): Promise<void> {
    const datumStr = datum.toISOString();
    const url = `${API_BASE_URL}/fhelix/imcprojects/addikberloeseforecast?jahr=${jahr}&datum=${datumStr}`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(items),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error adding IKB forecast revenues: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async deleteIkbErloeseItem(id: number): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/deleteikberloeseitem/${id}`;
    const res = await this.fetchWithAuth(url, {
      method: "DELETE",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error deleting IKB revenue item: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async updateIkbErloeseItem(item: PlanwertKostenErloeseItem): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/updateikberloeseitem`;
    const res = await this.fetchWithAuth(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error updating IKB revenue item: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async getIkbFinanzdatenItem(id: number): Promise<IkbFinanzdatenItem> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getikbfinanzdatenitem/${id}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving IKB financial data item: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<IkbFinanzdatenItem>;
  }

  async getIkbFinanzdaten(): Promise<IkbFinanzdatenItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getikbfinanzdaten`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving IKB financial data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<IkbFinanzdatenItem[]>;
  }

  async addIkbFinanzdaten(items: IkbFinanzdatenItem[]): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/addikbfinanzdaten`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(items),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error adding IKB financial data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async deleteIkbFinanzdatenItem(id: number): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/deleteikbfinanzdatenitem/${id}`;
    const res = await this.fetchWithAuth(url, {
      method: "DELETE",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error deleting IKB financial data item: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async updateIkbFinanzdatenItem(item: IkbFinanzdatenItem): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/updateikbfinanzdatenitem`;
    const res = await this.fetchWithAuth(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error updating IKB financial data item: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  // #endregion

  // #region SollIst-Vergleich (Plan vs Actual Comparison) Methods

  async getSollIstVergleichListe(
    projektId: number,
    schemaId: number = 1,
  ): Promise<SollIstVergleichItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getsollinstvergleichliste?projektId=${projektId}&schemaId=${schemaId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving plan vs actual comparison: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<SollIstVergleichItem[]>;
  }

  async getSollIstVergleichListeZeitraum(
    projektId: number,
    start: Date,
    ende: Date,
    schemaId: number = 1,
  ): Promise<SollIstVergleichItem[]> {
    const startStr = start.toISOString().split("T")[0];
    const endeStr = ende.toISOString().split("T")[0];
    const url = `${API_BASE_URL}/fhelix/imcprojects/getsollinstvergleichlistezeitraum?projektId=${projektId}&start=${startStr}&ende=${endeStr}&schemaId=${schemaId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving plan vs actual comparison for period: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<SollIstVergleichItem[]>;
  }

  async getPlanwerteSollIstVergleichListe(
    projektId: number,
    schemaId: number = 1,
    isUserProjektleiter: boolean = false,
  ): Promise<PlanwertKostenErloeseItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getplanwertsollinstvergleichliste?projektId=${projektId}&schemaId=${schemaId}&isUserProjektleiter=${isUserProjektleiter}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving planned values for plan vs actual comparison: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<PlanwertKostenErloeseItem[]>;
  }

  async addPlanwert(item: PlanwertKostenErloeseItem): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/addplanwert`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error adding planned value: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async updatePlanwert(item: PlanwertKostenErloeseItem): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/updateplanwert`;
    const res = await this.fetchWithAuth(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error updating planned value: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async deleteAllPlanwerte(projektId: number): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/deleteallplanwerte?projektId=${projektId}`;
    const res = await this.fetchWithAuth(url, {
      method: "DELETE",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error deleting all planned values: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async deletePlanwert(itemId: number): Promise<void> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/deleteplanwert?itemId=${itemId}`;
    const res = await this.fetchWithAuth(url, {
      method: "DELETE",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error deleting planned value: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  // #endregion

  // ==================== Head of Institute Methods ====================

  async getHeadsOfInstitute(): Promise<HeadOfInstituteItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getheadsofinstitute`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving heads of institute: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<HeadOfInstituteItem[]>;
  }

  async updateHeadsOfInstitute(items: HeadOfInstituteItem[]): Promise<void> {
    if (!items || items.length === 0) {
      throw new Error("HeadOfInstitute items array cannot be empty");
    }

    const url = `${API_BASE_URL}/fhelix/imcprojects/updateheadsofinstitute`;
    const res = await this.fetchWithAuth(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(items),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error updating heads of institute: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  // ==================== Info Groups Methods ====================

  async getInfoGroups(): Promise<InfoGroupItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getinfogroups`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving info groups: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<InfoGroupItem[]>;
  }

  async setInfoGroups(items: InfoGroupItem[]): Promise<void> {
    if (!items || items.length === 0) {
      throw new Error("Info groups array cannot be empty");
    }

    const url = `${API_BASE_URL}/fhelix/imcprojects/setinfogroups`;
    const res = await this.fetchWithAuth(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(items),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error updating info groups: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  // ==================== Freigabe Methods ====================

  async freigabeAngebot(projektId: number): Promise<boolean> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/freigabeangebot?projektId=${projektId}`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error requesting offer approval: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return true;
  }

  async projektKaufmannischBeenden(projektId: number): Promise<boolean> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/projektkaufmannischbeenden?projektId=${projektId}`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error commercially terminating project: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return true;
  }

  async projektOperativBeenden(projektId: number): Promise<boolean> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/projektoperativbeenden?projektId=${projektId}`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error operationally terminating project: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return true;
  }

  async freigabeInfoGroupCo(
    dokumentId: number,
    type: boolean,
    reason: string,
  ): Promise<boolean> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/freigabeinfogroupco?dokumentId=${dokumentId}&type=${type}`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(reason),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error processing document approval: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }

    // API returns plain text status message (e.g., "Document approved successfully")
    // Since status is OK (200), we can return true
    const responseText = await res.text();
    console.log("Approval response:", responseText);
    return true;
  }

  // ==================== Rechnungsdokument Methods ====================

  async getProjektrechnungBinaries(
    rechnungGuid: string,
    rechnungHeader: string,
    typePdf: boolean,
  ): Promise<{ blob: Blob; filename: string }> {
    if (!rechnungGuid) {
      throw new Error("Invoice GUID cannot be empty");
    }

    if (!rechnungHeader) {
      throw new Error("Invoice header parameter cannot be empty");
    }

    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektrechnungbinaries?rechnungGuid=${encodeURIComponent(rechnungGuid)}&rechnungHeader=${encodeURIComponent(rechnungHeader)}&typePdf=${typePdf}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });

    if (!res.ok) {
      if (res.status === 404) {
        throw new Error("Invoice document not found");
      }
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving invoice document: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }

    // Parse JSON response containing DocumentTemplateItem
    const dt = (await res.json()) as DocumentTemplateItem;

    if (!dt || !dt.imageBinary) {
      throw new Error("Invoice binary data not found in response");
    }

    // Determine file extension and MIME type based on typePdf parameter
    const fileExtension = typePdf ? ".pdf" : ".docx";
    const mimeType = typePdf
      ? "application/pdf"
      : "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

    // Extract filename from title and ensure correct extension
    let filename = dt.title || `invoice${fileExtension}`;

    // Remove existing extension if present and add the correct one
    if (
      filename.toLowerCase().endsWith(".pdf") ||
      filename.toLowerCase().endsWith(".docx")
    ) {
      filename = filename.substring(0, filename.lastIndexOf("."));
    }
    filename = `${filename}${fileExtension}`;

    // imageBinary from ASP.NET Core is serialized as Base64 string, decode it
    const binaryString = atob(dt.imageBinary);
    const bytes = new Uint8Array([...binaryString].map((c) => c.charCodeAt(0)));

    // Create Blob from binary data with correct MIME type
    const blob = new Blob([bytes], { type: mimeType });

    return { blob, filename };
  }

  async importRechnungToDocuments(rechnungId: number): Promise<boolean> {
    if (rechnungId <= 0) {
      throw new Error("Invoice ID must be greater than 0");
    }

    const url = `${API_BASE_URL}/fhelix/imcprojects/importrechnungtodocuments?rechnungId=${rechnungId}`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
    });

    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error importing invoice to documents: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }

    return true;
  }

  // ==================== Kalkulation Import Methods ====================

  async getKalkulationDataFromXlsImport(
    dokumentId: number,
  ): Promise<KalkulationImportData> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getkalkulationdatafromxlsimport?dokumentId=${dokumentId}`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving Kalkulation data from Excel import: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<KalkulationImportData>;
  }

  async importKalkulation(
    request: ImportKalkulationRequest,
  ): Promise<KalkulationImportResult> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/importkalkulation`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error importing Kalkulation: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    const result = (await res.json()) as KalkulationImportResult;
    return result;
  }

  // ==================== Mittelherkunft Methods ====================

  async deleteMittelherkunft(id: number): Promise<void> {
    if (id <= 0) {
      throw new Error("Mittelherkunft ID must be greater than 0");
    }

    const url = `${API_BASE_URL}/fhelix/imcprojects/deletemittelherkunft/${id}`;
    const res = await this.fetchWithAuth(url, {
      method: "DELETE",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error deleting Mittelherkunft: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async addMittelherkunft(id: number, bezeichnung: string): Promise<void> {
    if (id <= 0) {
      throw new Error("Mittelherkunft ID must be greater than 0");
    }

    if (!bezeichnung || bezeichnung.trim() === "") {
      throw new Error("Mittelherkunft description cannot be empty");
    }

    const url = `${API_BASE_URL}/fhelix/imcprojects/addmittelherkunft`;
    const request = {
      Id: id,
      Bezeichnung: bezeichnung,
    };
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error adding Mittelherkunft: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async updateMittelherkunft(id: number, bezeichnung: string): Promise<void> {
    if (id <= 0) {
      throw new Error("Mittelherkunft ID must be greater than 0");
    }

    if (!bezeichnung || bezeichnung.trim() === "") {
      throw new Error("Mittelherkunft description cannot be empty");
    }

    const url = `${API_BASE_URL}/fhelix/imcprojects/updatemittelherkunft?id=${id}`;
    const res = await this.fetchWithAuth(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(bezeichnung),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error updating Mittelherkunft: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async getMittelherkunftsItems(): Promise<IDataItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getmittelherkunftsitems`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving Mittelherkunft items: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<IDataItem[]>;
  }

  // ProjektRechnungKonto functions
  async deleteProjektRechnungKonto(id: number): Promise<void> {
    if (id <= 0) {
      throw new Error("ProjektRechnungKonto ID must be greater than 0");
    }

    const url = `${API_BASE_URL}/fhelix/imcprojects/deleteprojektrechnungkonto/${id}`;
    const res = await this.fetchWithAuth(url, {
      method: "DELETE",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error deleting ProjektRechnungKonto: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async addProjektRechnungKonto(item: ProjektRechnungKontoItem): Promise<void> {
    if (!item) {
      throw new Error("Valid ProjektRechnungKonto item is required");
    }

    const url = `${API_BASE_URL}/fhelix/imcprojects/addprojektrechnungkonto`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error adding ProjektRechnungKonto: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async updateProjektRechnungKonto(
    item: ProjektRechnungKontoItem,
  ): Promise<void> {
    if (!item) {
      throw new Error("Valid ProjektRechnungKonto item is required");
    }

    const url = `${API_BASE_URL}/fhelix/imcprojects/updateprojektrechnungkonto`;
    const res = await this.fetchWithAuth(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error updating ProjektRechnungKonto: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async getProjektRechnungKontoItems(): Promise<ProjektRechnungKontoItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektrechnungkontoitems`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving ProjektRechnungKonto items: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<ProjektRechnungKontoItem[]>;
  }

  // Foerdergeber functions
  async getProjektFoerdergeberItems(): Promise<FoerdergeberItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektfoerdergeberitems`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving Foerdergeber items: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<FoerdergeberItem[]>;
  }

  async updateProjektFoerdergeber(item: FoerdergeberItem): Promise<void> {
    if (!item) {
      throw new Error("Valid Foerdergeber item is required");
    }

    const url = `${API_BASE_URL}/fhelix/imcprojects/updateprojektfoerdergeber`;
    const res = await this.fetchWithAuth(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error updating Foerdergeber: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async addProjektFoerdergeber(item: FoerdergeberItem): Promise<void> {
    if (!item) {
      throw new Error("Valid Foerdergeber item is required");
    }

    const url = `${API_BASE_URL}/fhelix/imcprojects/addprojektfoerdergeber`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error adding Foerdergeber: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async deleteProjektFoerdergeber(id: number): Promise<void> {
    if (id <= 0) {
      throw new Error("Foerdergeber ID must be greater than 0");
    }

    const url = `${API_BASE_URL}/fhelix/imcprojects/deleteprojektfoerdergeber/${id}`;
    const res = await this.fetchWithAuth(url, {
      method: "DELETE",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error deleting Foerdergeber: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  // Stundensatz functions
  async deleteStundensatzItem(id: number): Promise<void> {
    if (id <= 0) {
      throw new Error("Stundensatz ID must be greater than 0");
    }

    const url = `${API_BASE_URL}/fhelix/imcprojects/deletestundensatzitem/${id}`;
    const res = await this.fetchWithAuth(url, {
      method: "DELETE",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error deleting Stundensatz: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async updateStundensatzItem(item: StundensatzItem): Promise<void> {
    if (!item) {
      throw new Error("Valid Stundensatz item is required");
    }

    const url = `${API_BASE_URL}/fhelix/imcprojects/updatestundensatzitem`;
    const res = await this.fetchWithAuth(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error updating Stundensatz: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async addStundensatzItem(item: StundensatzItem): Promise<void> {
    if (!item) {
      throw new Error("Valid Stundensatz item is required");
    }
    const url = `${API_BASE_URL}/fhelix/imcprojects/addstundensatzitem`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error adding Stundensatz: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
  }

  async getProjektStundensatzItem(id: number): Promise<StundensatzItem | null> {
    if (id <= 0) {
      throw new Error("Stundensatz ID must be greater than 0");
    }

    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektstundensatzitem/${id}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      if (res.status === 404) {
        return null;
      }
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving Stundensatz item: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<StundensatzItem>;
  }

  async getProjektStundensatzItems(): Promise<StundensatzItem[]> {
    const url = `${API_BASE_URL}/fhelix/imcprojects/getprojektstundensatzitems`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error retrieving Stundensatz items: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<StundensatzItem[]>;
  }

  async importStundensatzItems(
    file: File,
    dateVon: Date,
    dateBis: Date,
  ): Promise<{ importGuid: string; rowsProcessed: number; errors?: string[] }> {
    if (!file) {
      throw new Error("File is required");
    }

    const validTypes = [
      "text/plain",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/csv",
    ];
    if (!validTypes.includes(file.type)) {
      throw new Error("Invalid file type. Please upload a CSV or Excel file");
    }

    const url = `${API_BASE_URL}/fhelix/imcprojects/importstundensatzitems?dateVon=${encodeURIComponent(
      dateVon.toISOString(),
    )}&dateBis=${encodeURIComponent(dateBis.toISOString())}`;

    const formData = new FormData();
    formData.append("file", file);

    const response = await this.instance.acquireTokenSilent({
      scopes: scopes,
      account: this.account,
    });

    const res = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${response.accessToken}`,
      },
      body: formData,
    });

    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error importing Stundensatz items: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<{
      importGuid: string;
      rowsProcessed: number;
      errors?: string[];
    }>;
  }

  async setStatusOfProject(
    projektId: number,
    status: number,
  ): Promise<boolean> {
    if (projektId <= 0) {
      throw new Error("Project ID must be greater than 0");
    }
    if (status <= 0) {
      throw new Error("Status must be greater than 0");
    }

    const url = `${API_BASE_URL}/fhelix/imcprojects/setstatusofproject?projektId=${projektId}&status=${status}`;

    const response = await this.instance.acquireTokenSilent({
      scopes: scopes,
      account: this.account,
    });

    const res = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${response.accessToken}`,
      },
    });

    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error setting project status: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }

    return true;
  }
}
